import { useMemo } from "react";
import CohortCardView from "./view";
import CohortCardEdit from "./edit";
import ModalWrapper from "../../../../components/modal/modal";
import { useTranslation } from "react-i18next";

const CohortCard = ({
  cohort,
  isOpen,
  close,
  editMode,
  createCohort,
  updateCohort,
  deleteCohort,
  toEditMode,
  form,
}) => {
  const { t } = useTranslation();

  const create = () => {
    const { value, description } = form.getFieldsValue();

    if (!cohort.id) {
      createCohort({
        value,
        description,
      });
    } else {
      updateCohort(cohort.id, {
        ...cohort,
        value,
        description,
      });
    }
  };

  const associationGroups = useMemo(() => {
    if (!cohort) return [];

    if (cohort.associations) {
      const src = cohort.associations;

      if (Array.isArray(src)) {
        return src
          .filter(Boolean)
          .map((g) => ({
            key: g.key || g.title,
            title: g.title,
            count: Number.isInteger(g.count) ? g.count : undefined,
            items: Array.isArray(g.items)
              ? g.items.filter(Boolean).map((it) => ({
                  id: it.id,
                  label: it.label,
                  href: it.href,
                  icon: it.icon,
                  onClick: it.onClick,
                }))
              : [],
          }))
          .filter((g) => g.title);
      }

      if (typeof src === "object") {
        return Object.entries(src)
          .map(([key, g]) => ({
            key,
            title: g?.title || key,
            count: Number.isInteger(g?.count) ? g.count : undefined,
            items: Array.isArray(g?.items)
              ? g.items.map((it) => ({
                  id: it.id,
                  label: it.label,
                  href: it.href,
                  icon: it.icon,
                  onClick: it.onClick,
                }))
              : [],
          }))
          .filter((g) => g.title);
      }
    }

    const normalizeItems = (arr = [], labelKey = "title") =>
      (arr || []).filter(Boolean).map((item) => ({
        id: item?.id,
        label:
          item?.label ||
          item?.title ||
          item?.name ||
          item?.value ||
          item?.displayId ||
          "—",
        href: item?.href,
        icon: item?.icon,
      }));

    const groups = [
      {
        key: "tasks",
        title: "Tasks",
        items: normalizeItems(cohort?.tasks),
      },
      {
        key: "onboardingSurveys",
        title: "Onboardings",
        items: normalizeItems(cohort?.onboardingSurveys),
      },
      {
        key: "plans",
        title: "Plans",
        items: normalizeItems(cohort?.plans),
      },
      {
        key: "subjects",
        title: "Subjects",
        items: normalizeItems(cohort?.subjects),
      },
    ]
      .map((g) => ({
        ...g,
        count: g.items.length,
      }))
      .filter((g) => g.count > 0);

    return groups;
  }, [cohort]);

  return (
    <ModalWrapper
      isOpen={isOpen}
      title={cohort.id ? "Cohort" : "Create cohort"}
      onOk={create}
      onCancel={close}
      okText={t(
        cohort.id ? "categories.modal.update" : "categories.modal.create"
      )}
      okButtonProps={{
        "data-testid": "cohort-save",
        size: "large",
        style: { display: editMode ? "" : "none" },
      }}
      cancelButtonProps={{
        size: "large",
        style: {
          backgroundColor: "rgba(108, 108, 137, 0.08)",
          display: editMode ? "" : "none",
        },
      }}
    >
      {editMode ? (
        <CohortCardEdit
          cohort={cohort}
          createCohort={createCohort}
          close={close}
          updateCohort={updateCohort}
          deleteCohort={deleteCohort}
          form={form}
        />
      ) : (
        <CohortCardView
          cohort={cohort}
          close={close}
          toEditMode={toEditMode}
          deleteCohort={deleteCohort}
          form={form}
          associationGroups={associationGroups}
        />
      )}
    </ModalWrapper>
  );
};

export default CohortCard;